// Copyright 2025 Imprivata, Inc. All rights reserved.

import type { ImportResult } from '@api/types';
import { Button, Modal } from '@imprivata-cloud/components';
import { Form } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorCode } from '@/errorHandler/constants';
import { useErrorContext } from '@/errorHandler/context/ErrorContext';
import { AppError } from '@/errorHandler/errors';
import { FILES_DOWNLOAD_NAMES } from '@/portal/constants';
import { downloadAsFileToUserComputer } from '@/utils/utils';
import DownloadImg from '../../../../../assets/icons/download.svg?react';
import CsvUpload from '../../../../../components/csvUpload/CsvUpload';
import { useSubmitUsersCsv, useUsersImportTemplate } from '../../hooks/useUsersImport';
import ImportUsersLoading from './ImportUsersLoading';
import ImportUsersResult from './ImportUsersResult';
import './ImportUsersModal.less';
import { useSession } from '@/common/authorization/context/Session';

const TRANSLATION_ROOT = 'portal.settings.identity-users.import-users-modal';
const IMPORT_STEP = { UPLOAD: 'UPLOAD', LOADING: 'LOADING', RESULT: 'RESULT' } as const;

interface ImportUsersModalProps {
  open: boolean;
  onClose: () => void;
  onImportFinished: () => void;
}

const ImportUsersModal = ({ open, onClose, onImportFinished }: ImportUsersModalProps) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const { tenantId } = useSession();
  const [csvFile, setCsvFile] = useState<File | undefined>();
  const { emitError } = useErrorContext();
  const [importResult, setImportResult] = useState<ImportResult>();
  const [errorReport, setErrorReport] = useState<Blob | undefined>();
  const [step, setStep] = useState<keyof typeof IMPORT_STEP>(IMPORT_STEP.UPLOAD);
  const { refetch: refetchTemplate } = useUsersImportTemplate(tenantId || '');
  const { mutate: submitCsv } = useSubmitUsersCsv(tenantId || '');

  //Download template
  const handleDownloadTemplate = async () => {
    try {
      const { data } = await refetchTemplate();
      if (!data) {
        return;
      }
      downloadAsFileToUserComputer(data, FILES_DOWNLOAD_NAMES.USERS_IMPORT_TEMPLATE);
    } catch (e) {
      console.log('Error downloading users import template:', e);
      emitError(new AppError({ code: ErrorCode.UNKNOWN }));
    }
  };

  // Import CSV
  const handleImport = () => {
    if (!csvFile) {
      return;
    }
    setStep(IMPORT_STEP.LOADING);
    submitCsv(
      { importFile: csvFile },
      {
        onSuccess: ({ importResult, errorReport }) => {
          console.log('[onSuccess] importResult: ', importResult);
          setImportResult(importResult);
          setErrorReport(errorReport);
          setStep(IMPORT_STEP.RESULT);
        },
        onError: (e: unknown) => {
          console.log('Error importing users CSV:', e);
          emitError(new AppError({ code: ErrorCode.USERS_IMPORT_FAIL }));
          setStep(IMPORT_STEP.UPLOAD);
          setCsvFile(undefined);
        },
      },
    );
  };

  // Which content to render: Upload CSV -> Loading -> Result
  const renderContent = () => {
    switch (step) {
      case IMPORT_STEP.LOADING:
        return <ImportUsersLoading fileName={csvFile?.name ?? ''} />;

      case IMPORT_STEP.RESULT:
        return (
          <ImportUsersResult
            importResult={importResult}
            errorReport={errorReport}
            onDone={() => {
              onImportFinished();
              onClose();
            }}
          />
        );

      default: {
        const requirements = t(`${TRANSLATION_ROOT}.file-requirements`, { returnObjects: true });
        const requirementList = Array.isArray(requirements) ? requirements : [];
        return (
          <>
            <div className="modal-content">
              <p>{t(`${TRANSLATION_ROOT}.upload-csv`)}</p>

              {/* CSV Upload component */}
              <CsvUpload
                fieldName="usersCsv"
                fieldValue={form.getFieldValue('usersCsv')}
                onFileChange={setCsvFile}
              />

              {/* File requirements */}
              <label className="requirements-title">
                {t(`${TRANSLATION_ROOT}.file-requirements-title`)}
              </label>
              <ul className="requirements-list">
                {requirementList.map(item => (
                  <li key={item}>{item}</li>
                ))}
              </ul>

              {/* Download template */}
              <Button
                aria-label="button-download-template"
                className="button-download-template"
                onClick={handleDownloadTemplate}
                icon={<DownloadImg />}
                label={t(`${TRANSLATION_ROOT}.download-template`)}
                size="small"
                type="link"
              />
            </div>

            {/* Footer */}
            <div className="modal-footer">
              <Button
                aria-label="button-import"
                label={t(`${TRANSLATION_ROOT}.import-button`)}
                disabled={!csvFile}
                size="small"
                type="primary"
                onClick={handleImport}
              />
              <Button
                aria-label="button-cancel"
                onClick={handleClose}
                size="small"
                label={t('common.cancel')}
              />
            </div>
          </>
        );
      }
    }
  };

  // Close modal
  const handleClose = () => {
    if (step === IMPORT_STEP.RESULT) {
      onImportFinished();
    }
    onClose();
  };

  return (
    <Modal
      aria-label="import-users-modal"
      className="import-users-modal"
      footer={null}
      title={t(`${TRANSLATION_ROOT}.title`)}
      onCancel={handleClose}
      open={open}
      width={800}
      destroyOnHidden
      maskClosable={false}
    >
      {renderContent()}
    </Modal>
  );
};

export default ImportUsersModal;
